"use client";

/**
 * `useAutosave` — debounced localStorage persistence for in-flight form
 * drafts.
 *
 * The hook does two things:
 *
 *  1. **On mount**, it reads `localStorage["aio:formdraft:" + formId]`
 *     once and exposes the parsed draft via the returned `draft`. The
 *     caller hydrates rhf via `defaultValues={draft ?? initial}` on the
 *     same render — autosave is meant to feel like "your form was
 *     restored from where you left off", which only works if hydration
 *     happens before the first paint of the form.
 *
 *  2. **On every `values` change**, it (re-)schedules a write of the
 *     latest values to the same key, debounced by `debounceMs`
 *     (default 1000 ms). Rapid keystrokes therefore collapse to one
 *     write per debounce window — typical "save draft on idle"
 *     behaviour. The timer is stored in a `useRef` so successive
 *     scheduling reuses (and resets) it instead of stacking timers.
 *
 *  3. The returned `clear()` removes the persisted draft. Callers
 *     invoke it from their `onSubmit` success path so a saved record
 *     doesn't leave a stale draft behind for the next session.
 *
 * ## Why localStorage and not sessionStorage / IndexedDB
 *
 * `localStorage` is synchronous, supported everywhere, and surfaces a
 * single string key/value. The total form-state size is small enough
 * that the 5–10 MB browser quota is never a concern. `sessionStorage`
 * would lose drafts when the tab closes — exactly the case where
 * autosave should save the user. IndexedDB is async and requires a
 * larger surface for what amounts to "stash one JSON blob".
 *
 * Per spec §9.1 we do not silently fallback for domain failures, but
 * this hook deliberately does swallow `SyntaxError` / `QuotaExceededError`
 * — the autosave path is best-effort, not a critical-path persistence
 * boundary, and a corrupt/full localStorage entry should not crash the
 * form. The corrupt-JSON case returns `draft = undefined` (the same as
 * "no draft"), which is the right semantics: we can't restore garbage,
 * so we treat it as absent.
 *
 * ## Read-on-mount semantics
 *
 * The draft is captured via `useState` initialiser so `draft` stays
 * stable across the component's lifetime. Subsequent writes are not
 * reflected in the returned `draft` — that's intentional, since the
 * caller has already hydrated into rhf and the rhf state is now the
 * source of truth. If a caller mounts a fresh form against the same
 * `formId` later in the session (rare; tab-restored navigation), they
 * get a fresh hook instance and a fresh read.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Namespace prefix for autosave keys. Centralising the format means a
 * future migration (e.g. adding a versioning suffix when the draft
 * shape changes) can ship in one place.
 */
const KEY_PREFIX = "aio:formdraft:";

/**
 * Build the localStorage key for a given form id. Exported so callers
 * (and tests) can reason about the key without knowing the prefix.
 */
export function autosaveStorageKey(formId: string): string {
  return `${KEY_PREFIX}${formId}`;
}

/**
 * Try to read and JSON-parse a stored draft. Returns `undefined` on
 * any read or parse error — see module docstring for rationale.
 */
function readDraft<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    // Corrupt JSON, blocked storage, or storage access denied. Treat as
    // absent rather than crashing the mount. We also do not log here:
    // this is best-effort and a noisy log per render would clutter the
    // console for an entirely user-recoverable situation.
    return undefined;
  }
}

export interface UseAutosaveOptions<TValues> {
  /**
   * Stable identifier for this form instance. Different routes with
   * different draft semantics MUST use distinct ids; the same route
   * across reloads MUST use the same id. Typical pattern:
   * `<module>:<entity>:new` or `<module>:<entity>:<recordId>`.
   */
  formId: string;
  /** The current form values. The hook persists whatever shape the caller passes. */
  values: TValues;
  /**
   * Debounce window in milliseconds. Defaults to 1000 — small enough
   * that a typist's pause registers, large enough that a rapid burst
   * (autocomplete, paste) collapses to one write.
   */
  debounceMs?: number;
}

export interface UseAutosaveResult<TValues> {
  /**
   * The draft value read from localStorage on mount, or `undefined` if
   * none was stored. Stable across the hook's lifetime — write-back is
   * not reflected here (rhf becomes the source of truth post-hydration).
   */
  draft: TValues | undefined;
  /**
   * Removes the persisted draft. Call from the post-submit success
   * path so the next mount starts clean.
   */
  clear: () => void;
}

export function useAutosave<TValues>({
  formId,
  values,
  debounceMs = 1000,
}: UseAutosaveOptions<TValues>): UseAutosaveResult<TValues> {
  const key = autosaveStorageKey(formId);

  // Capture once on mount so callers can safely use `draft` as a
  // hydration source on the same render. We read against the FIRST
  // formId — if the caller swaps formId mid-session, that's effectively
  // a different form and they should remount the hook.
  const [draft] = useState<TValues | undefined>(() => readDraft<TValues>(key));

  // Hold the debounce timer across renders. We use the global setTimeout
  // typing rather than `NodeJS.Timeout` because both jsdom and browsers
  // return a number-shaped handle and we don't need NodeJS ambient types
  // in this client-only file.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Reset any pending write — we only want one in flight per debounce
    // window. Without this reset, successive renders would stack timers
    // and write multiple times after the burst settles.
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(values));
      } catch {
        // Quota exceeded / storage unavailable. The form keeps working;
        // the user only loses the autosave safety net, which is the
        // right tradeoff (best-effort by design).
      }
      timerRef.current = null;
    }, debounceMs);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [key, values, debounceMs]);

  const clear = useCallback(() => {
    // Cancel any pending debounce so it cannot resurrect the entry
    // immediately after we remove it.
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // Same best-effort posture as the write path.
    }
  }, [key]);

  return { draft, clear };
}
